import type {RefObject} from 'react';
import {useEffect, useLayoutEffect, useMemo, useState} from 'react';
import type {GraphicsMetaMapType} from '@Components/social-media/publish-params.types';
import type {GraphicsChangeDependencies} from './social-post-content-multiposting.types';
import {getPreviewScaleForDesignWidth} from './social-post-content-multiposting.library';

/** Scales a preview card of {@link designWidthPx} down to whatever width the parent leaves it, tracking parent resizes. */
export function usePreviewScaleForParentWidth(parentContainerRef: RefObject<HTMLElement | null> | undefined, previewWidthInsetPx: number, designWidthPx: number): number {
  const [parentWidth, setParentWidth] = useState(0);

  useEffect((): VoidFunction => {
    const parentContainer = parentContainerRef?.current;

    const measureParentWidth = (): void => {
      setParentWidth(parentContainer?.clientWidth ?? 0);
    };

    measureParentWidth();

    const parentResizeObserver = new ResizeObserver(measureParentWidth);

    if (parentContainer) {
      parentResizeObserver.observe(parentContainer);
    }

    return (): void => {
      parentResizeObserver.disconnect();
    };
  }, [parentContainerRef?.current]);

  return getPreviewScaleForDesignWidth(parentWidth - previewWidthInsetPx, designWidthPx);
}

export function useIsElementContentClamped(elementRef: RefObject<HTMLElement | null>, content: string): boolean {
  const [isClamped, setIsClamped] = useState(false);

  useLayoutEffect((): VoidFunction | undefined => {
    const element = elementRef.current;

    if (!element) {
      return undefined;
    }

    const measureClamping = (): void => {
      setIsClamped(element.scrollHeight > element.clientHeight || element.scrollWidth > element.clientWidth);
    };

    measureClamping();

    const elementResizeObserver = new ResizeObserver(measureClamping);
    elementResizeObserver.observe(element);

    return (): void => {
      elementResizeObserver.disconnect();
    };
  }, [content]);

  return isClamped;
}

export function useGraphicsSignature(designsGraphicMetaMap: GraphicsMetaMapType): string {
  return useMemo(() => JSON.stringify(designsGraphicMetaMap), [designsGraphicMetaMap]);
}

export function useResetLoadingOnGraphicsChange(dependencies: GraphicsChangeDependencies, resetLoading: VoidFunction): void {
  useEffect((): void => {
    resetLoading();
  }, [dependencies.accountId, dependencies.platformMediaType, dependencies.graphicsSignature]);
}
